import type { FactsField, FactsItemField, FactsSchema, OpeningDay } from "./types";

/**
 * The words a facts form and a facts version are read in, whoever reads them: the days, why a value
 * fails, and a stored value as one line of text. Build round 18 (2026-10-07), decision 0503.
 *
 * Moved here from `app/routes/adminClient/FactsVocabulary.ts` in build round 19 (2026-10-08), when
 * the client's Webbplatsens fakta began to draw the same controls and read back the same versions.
 * What is Petrovic's alone (why a schema file was refused, why a read of it failed, the type names
 * the schema section lists) stayed there, because a client never reads a schema.
 *
 * Every code here is one `docs/facts-schema.md` defines. A code not listed is shown as itself rather
 * than guessed at.
 */

export const DAY_LABEL: Record<OpeningDay, string> = {
  mon: "Måndag",
  tue: "Tisdag",
  wed: "Onsdag",
  thu: "Torsdag",
  fri: "Fredag",
  sat: "Lördag",
  sun: "Söndag"
};

/** The short form, for a version's values read back as one line. */
export const DAY_SHORT: Record<OpeningDay, string> = {
  mon: "mån",
  tue: "tis",
  wed: "ons",
  thu: "tor",
  fri: "fre",
  sat: "lör",
  sun: "sön"
};

/**
 * Why a value fails, beside its field. A field is passed when the sentence can name its own limit, so
 * "too long" says how long is allowed. Without one (a key the schema no longer has) the sentence is
 * the general one.
 */
export function valueProblemSentence(code: string, field?: FactsField | FactsItemField): string {
  switch (code) {
    case "values_not_object":
      return "Värdena är inte ett objekt.";
    case "unknown_key":
      return "Värdet hör inte till något fält i schemat. Det försvinner när utkastet sparas.";
    case "required":
      return field?.type === "list" ? "Listan måste ha minst en rad." : "Fältet måste fyllas i.";
    case "wrong_type":
      if (field?.type === "number") {
        return "Skriv ett tal, med komma eller punkt före decimalerna.";
      }
      if (field?.type === "price") {
        return "Skriv beloppet som ett tal, med komma eller punkt före decimalerna.";
      }
      return "Värdet har fel form för fältet.";
    case "control_character":
      return "Texten får inte ha radbrytningar eller andra styrtecken.";
    case "blank":
      return "Texten består bara av mellanslag. Töm fältet om det inte ska ha något värde.";
    case "too_long":
      return field?.max_length ? `Högst ${field.max_length} tecken.` : "Texten är för lång.";
    case "not_allowed_value":
      return "Värdet är inte ett av dem schemat tillåter.";
    case "not_integer":
      return "Bara heltal.";
    case "out_of_range":
      if (field?.type === "number" && field.min !== undefined && field.max !== undefined) {
        return `Talet måste vara från ${formatNumber(field.min)} till ${formatNumber(field.max)}.`;
      }
      if (field?.type === "price" && field.max !== undefined) {
        return `Beloppet måste vara från 0 till ${formatNumber(field.max)}.`;
      }
      return "Värdet ligger utanför det tillåtna.";
    case "too_many_decimals":
      return "Högst två decimaler.";
    case "price_fixed":
      return "Från-pris och moms är låsta i schemat och kan inte ändras här.";
    case "opening_hours_invalid":
      return "Öppettiderna har fel form.";
    case "time_invalid":
      return "Skriv både när det öppnar och när det stänger, som TT:MM, eller kryssa i Stängt.";
    case "time_order":
      return "Det måste stänga efter att det öppnar, samma dag.";
    case "phone_invalid":
      return "Skriv ett svenskt telefonnummer, till exempel 070-123 45 67 eller 08-123 456 78.";
    case "email_invalid":
      return "Skriv en e-postadress, till exempel info@foretaget.se.";
    case "url_invalid":
      return "Skriv en hel adress som börjar med https://, utan mellanslag.";
    case "too_few_items":
      return field?.type === "list" && field.min_items !== undefined
        ? `Minst ${field.min_items} ${field.min_items === 1 ? "rad" : "rader"}.`
        : "För få rader.";
    case "too_many_items":
      return field?.type === "list" && field.max_items !== undefined
        ? `Högst ${field.max_items} ${field.max_items === 1 ? "rad" : "rader"}.`
        : "För många rader.";
    default:
      return `Kod: ${code}.`;
  }
}

type JsonObject = Record<string, unknown>;

const isObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function describePrice(price: JsonObject): string {
  const amount = typeof price.amount === "number" ? `${formatNumber(price.amount)} kr` : String(price.amount);
  return `${price.from === true ? "från " : ""}${amount} ${price.incl_vat === true ? "inklusive moms" : "exklusive moms"}`;
}

function describeDay(entry: unknown): string {
  if (!isObject(entry) || typeof entry.day !== "string") {
    return JSON.stringify(entry);
  }
  const day = DAY_SHORT[entry.day as OpeningDay] ?? entry.day;
  return entry.closed === true ? `${day} stängt` : `${day} ${String(entry.from)}–${String(entry.to)}`;
}

/**
 * A stored value as one line of text, for a published version read back. The field is the current
 * schema's when it still has one by that key, which gives a list's items their labels; a version
 * published under another schema is still shown, by its shape. Never markup: the caller renders the
 * string as text.
 */
export function describeValue(value: unknown, field?: FactsField | FactsItemField): string {
  if (value === null || value === undefined) {
    return "Inget värde";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return formatNumber(value);
  }
  if (isObject(value) && "amount" in value) {
    return describePrice(value);
  }
  if (Array.isArray(value) && value.every((entry) => isObject(entry) && "day" in entry)) {
    return value.map(describeDay).join(", ");
  }
  if (Array.isArray(value)) {
    const items = field?.type === "list" ? (field.item_fields ?? []) : [];
    return value
      .map((row: unknown, index) => {
        if (!isObject(row)) {
          return `${index + 1}) ${JSON.stringify(row)}`;
        }
        const parts = Object.keys(row).map((key) => {
          const item = items.find((candidate) => candidate.key === key);
          return `${item?.label ?? key}: ${describeValue(row[key], item)}`;
        });
        return `${index + 1}) ${parts.join(", ")}`;
      })
      .join("; ");
  }
  return JSON.stringify(value);
}

/** A number the Swedish way: a comma before the decimals and a space between thousands. */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 2, useGrouping: true }).format(value);
}

/**
 * The schema's own words for a problem's path, with the field it names when there is one: a field's
 * label, "<list> rad N" for a row or a day, and "<list> rad N, <item field>" inside a row; a key the
 * schema no longer has is named and said to be gone. Build round 20 (2026-10-08), for the Fakta sheet's
 * "Väntar på fakta", and the same words `private.site_facts_problem_label` writes into the alert's
 * title, so the sheet and the alert that led to it name a field alike.
 */
export function problemFieldLabel(
  schema: FactsSchema,
  path: string
): { label: string; field?: FactsField | FactsItemField } {
  const parts = /^([a-z][a-z0-9_]*)(?:\[([0-9]+)\](?:\.([a-z][a-z0-9_]*))?)?$/.exec(path);
  const field = parts ? schema.fields.find((candidate) => candidate.key === parts[1]) : undefined;
  if (!parts) {
    return { label: path };
  }
  if (!field) {
    // A key the schema no longer has, as after a rename. The alert's title names it the same way
    // (`private.site_facts_problem_label`).
    return { label: `${parts[1]} (finns inte längre i schemat)` };
  }
  if (parts[2] === undefined) {
    return { label: field.label, field };
  }
  const row = `${field.label} rad ${Number(parts[2]) + 1}`;
  if (parts[3] === undefined) {
    // A day of opening hours or a list's row as a whole: the field's own sentences still apply.
    return { label: row, field };
  }
  const item = field.item_fields?.find((candidate) => candidate.key === parts[3]);
  return { label: `${row}, ${item?.label ?? parts[3]}`, field: item };
}
